import { ArrowTopRightOnSquareIcon, DocumentTextIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline"
import { EllipsisVerticalIcon } from "@heroicons/react/24/solid"
import React, { useState } from "react"
import { ReactSortable } from "react-sortablejs"
import { useMutation } from "urql"

import { Button, IconButton } from "../../components/shared/Buttons"
import Typography from "../../components/shared/Typography"
import { useToast } from "../../contexts/ToastContext"
import GraphQLProvider from "../../providers/GraphQLProvider"
import { formatBlogPostDate } from "../Blog/blogDateUtils"
import { buildPublicBlogPostUrl } from "../Blog/buildPublicBlogPostUrl"
import ProfileEditProvider from "../ProfileEdit/ProfileEditProvider"

import BlogPageTitleButton from "./BlogPageSettings"
import BlogPostForm from "./BlogPostForm"

const UPSERT_BLOG_POST_MUTATION = `
  mutation UpsertBlogPost(
    $id: ID
    $title: String!
    $subtitle: String
    $content: String!
    $slug: String
    $tags: [String!]
    $photoHandle: String
    $clearPhoto: Boolean
    $published: Boolean
    $publishedAt: ISO8601Date
  ) {
    upsertBlogPost(
      id: $id
      title: $title
      subtitle: $subtitle
      content: $content
      slug: $slug
      tags: $tags
      photoHandle: $photoHandle
      clearPhoto: $clearPhoto
      published: $published
      publishedAt: $publishedAt
    ) {
      result
      blogPost {
        id
        title
        subtitle
        slug
        content
        tags
        photoUrl
        publishedAt
        published
        position
      }
      errors
    }
  }
`

const REORDER_BLOG_POSTS_MUTATION = `
  mutation ReorderBlogPosts($blogPostsArray: [String!]!) {
    reorderBlogPosts(blogPostsArray: $blogPostsArray) {
      result
      errors
    }
  }
`

const DELETE_BLOG_POST_MUTATION = `
  mutation DeleteBlogPost($id: ID!) {
    deleteBlogPost(id: $id) {
      result
      errors
    }
  }
`

const sortPostsByPosition = (posts) => [...posts].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))

const BlogPostList = ({ blogPosts, setBlogPosts, onEdit, onDelete, publicSiteBaseUrl }) => {
  const { showToast } = useToast()
  const [, reorderBlogPosts] = useMutation(REORDER_BLOG_POSTS_MUTATION)

  if (blogPosts.length === 0) {
    return (
      <Typography variant="subtitle" className="text-gray-dark">
        No blog posts yet. Create your first article.
      </Typography>
    )
  }

  return (
    <div>
      <Typography variant="small" className="mb-3 text-gray-dark">
        Drag posts to set the order visitors see on your public blog.
      </Typography>
      <ReactSortable
        className="flex flex-col gap-4"
        list={blogPosts}
        setList={(newOrder) => {
          if (JSON.stringify(newOrder) === JSON.stringify(blogPosts)) return

          const blogPostsArray = newOrder.map((post, index) => JSON.stringify({ id: post.id, position: index + 1 }))
          reorderBlogPosts({ blogPostsArray }).then((result) => {
            if (result?.data?.reorderBlogPosts?.result !== "success") {
              showToast({ type: "error", content: "Could not save post order. Please try again." })
            }
          })
          setBlogPosts(newOrder.map((post, index) => ({ ...post, position: index + 1 })))
        }}>
        {blogPosts.map((post) => (
          <div
            key={post.id}
            className="flex cursor-grab gap-2 rounded-lg border border-gray-light bg-white p-4 sm:gap-4">
            <EllipsisVerticalIcon className="mt-1 size-6 shrink-0 text-gray-dark" aria-hidden />
            {post.photoUrl && <img src={post.photoUrl} alt="" className="h-20 w-28 shrink-0 rounded-lg object-cover" />}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <Typography variant="h5">{post.title}</Typography>
                {!post.published && !post.publishedAt && (
                  <span className="rounded-full bg-builder-border-light px-2 py-0.5 text-xs font-bold text-builder-secondary">
                    Draft
                  </span>
                )}
              </div>
              <div className="mt-1 flex flex-col gap-1">
                {post.subtitle && (
                  <Typography variant="small" as="p" className="text-gray-dark">
                    {post.subtitle}
                  </Typography>
                )}
                <Typography variant="small" as="p" className="text-gray-dark">
                  {[formatBlogPostDate(post.publishedAt), `/blog/${post.slug}`].filter(Boolean).join(" · ")}
                </Typography>
              </div>
              {post.tags?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-builder-border-light px-2 py-0.5 text-xs text-builder-secondary">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <IconButton
                Icon={ArrowTopRightOnSquareIcon}
                tooltip={post.published ? "View on site" : "Publish to view on site"}
                disabled={!post.published}
                onClick={() => {
                  if (!post.published || !post.slug) return
                  window.open(buildPublicBlogPostUrl(post.slug, publicSiteBaseUrl), "_blank", "noopener,noreferrer")
                }}
              />
              <IconButton Icon={PencilIcon} onClick={() => onEdit(post)} tooltip="Edit" />
              <IconButton Icon={TrashIcon} onClick={() => onDelete(post)} tooltip="Delete" />
            </div>
          </div>
        ))}
      </ReactSortable>
    </div>
  )
}

const PortalBlogPosts = ({
  blogPosts: initialPosts,
  practice = {},
  blogPageTitle: initialBlogPageTitle = "",
  publicSiteBaseUrl = ""
}) => {
  const [blogPosts, setBlogPosts] = useState(() => sortPostsByPosition(initialPosts || []))
  const [blogPageTitle, setBlogPageTitle] = useState(initialBlogPageTitle || "")
  const [editorPost, setEditorPost] = useState(null)
  const [isCreating, setIsCreating] = useState(initialPosts.length === 0)
  const [saving, setSaving] = useState(false)
  const { showToast } = useToast()

  const [, upsertBlogPost] = useMutation(UPSERT_BLOG_POST_MUTATION)
  const [, deleteBlogPost] = useMutation(DELETE_BLOG_POST_MUTATION)

  const inEditor = isCreating || editorPost

  const closeEditor = () => {
    setIsCreating(false)
    setEditorPost(null)
  }

  const persistPost = async (formData, { closeOnSuccess = true, successMessage } = {}) => {
    const { published, ...payload } = formData
    setSaving(true)
    try {
      const result = await upsertBlogPost({ ...payload, published })
      if (result.data?.upsertBlogPost?.result === "success") {
        const saved = {
          ...result.data.upsertBlogPost.blogPost,
          published: Boolean(result.data.upsertBlogPost.blogPost.published)
        }
        if (editorPost) {
          setBlogPosts((posts) => sortPostsByPosition(posts.map((p) => (p.id === saved.id ? saved : p))))
        } else {
          setBlogPosts((posts) => sortPostsByPosition([...posts, saved]))
        }
        setEditorPost(saved)
        setIsCreating(false)
        if (successMessage) {
          showToast({ content: successMessage, type: "success" })
        }
        if (closeOnSuccess) closeEditor()
        return saved
      }

      const error = result.data?.upsertBlogPost?.errors?.[0] || "Failed to save blog post"
      showToast({ content: error, type: "error" })
      return null
    } catch (error) {
      console.error(error) // eslint-disable-line no-console
      showToast({ content: "Failed to save blog post", type: "error" })
      return null
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async (formData) => {
    const message = formData.published ? "Blog post published" : "Draft saved"
    await persistPost(formData, { closeOnSuccess: true, successMessage: message })
  }

  const handlePreview = async (formData) => {
    const saved = await persistPost(
      { ...formData, published: formData.published ?? false },
      { closeOnSuccess: false, successMessage: "Draft saved for preview" }
    )
    if (!saved?.id) return

    if (saved.published && saved.slug) {
      window.open(buildPublicBlogPostUrl(saved.slug, publicSiteBaseUrl), "_blank", "noopener,noreferrer")
      return
    }

    window.open(`/portal/website/blog/preview/${saved.id}`, "_blank", "noopener,noreferrer")
  }

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"?`)) return

    const result = await deleteBlogPost({ id: post.id })
    if (result.data?.deleteBlogPost?.result === "success") {
      setBlogPosts((posts) => posts.filter((p) => p.id !== post.id))
      showToast({ content: "Blog post deleted", type: "success" })
      if (editorPost?.id === post.id) closeEditor()
    } else {
      showToast({ content: result.data?.deleteBlogPost?.errors?.[0] || "Failed to delete", type: "error" })
    }
  }

  const isFirstPost = blogPosts.length === 0 && isCreating && !editorPost

  if (inEditor) {
    return (
      <div className="-mx-4 min-h-[calc(100vh-4.5rem)] px-2 py-4 md:-mx-4 md:px-4 md:py-8">
        <div className="mx-auto max-w-splash-container">
          {!isFirstPost && (
            <button
              type="button"
              onClick={closeEditor}
              className="mb-6 text-sm font-bold text-teal hover:underline"
              disabled={saving}>
              ← All posts
            </button>
          )}

          {isFirstPost && (
            <div
              className="mb-8 flex gap-4 rounded-xl border border-teal/25 bg-white p-5 shadow-sm md:p-6"
              role="status">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-teal/10">
                <DocumentTextIcon className="size-7 text-teal" aria-hidden />
              </div>
              <div className="min-w-0">
                <p className="text-lg font-bold text-builder-primary md:text-xl">
                  Let&apos;s create your first blog post!
                </p>
                <p className="mt-1 text-sm text-builder-secondary md:text-base">
                  Share your expertise with visitors. When you publish, your blog will appear on your website and in
                  your site navigation.
                </p>
              </div>
            </div>
          )}

          <div className="mb-10">
            <div className="mb-2 flex items-center gap-3">
              <DocumentTextIcon className="size-7 text-builder-primary md:size-8" />
              <h1 className="text-2xl font-bold text-builder-primary md:text-[32px]">
                {editorPost ? "Edit blog post" : "Create new blog post"}
              </h1>
            </div>
            <p className="text-sm text-builder-secondary md:text-base">
              Fill in the details below to create and publish your blog post.
            </p>
          </div>

          <BlogPostForm
            post={editorPost}
            practice={practice}
            saving={saving}
            onSave={handleSave}
            onCancel={closeEditor}
            onPreview={handlePreview}
          />
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-8 flex items-center justify-between sm:flex-col sm:items-start">
        <div>
          <Typography variant="h3" as="h1">
            Blog
          </Typography>
          <Typography variant="subtitle">Create articles for your website visitors.</Typography>
          {blogPageTitle && (
            <Typography variant="small" className="text-gray-dark">
              Public title on /blog: <span className="font-semibold">{blogPageTitle}</span>
            </Typography>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:mt-4">
          <BlogPageTitleButton initialTitle={blogPageTitle} onSaved={(value) => setBlogPageTitle(value || "")} />
          <Button type="primary" onClick={() => setIsCreating(true)}>
            New article
          </Button>
        </div>
      </div>

      <BlogPostList
        blogPosts={blogPosts}
        setBlogPosts={setBlogPosts}
        onEdit={setEditorPost}
        onDelete={handleDelete}
        publicSiteBaseUrl={publicSiteBaseUrl}
      />
    </div>
  )
}

export default function PortalBlogPostsPage(props) {
  return (
    <GraphQLProvider>
      <ProfileEditProvider>
        <PortalBlogPosts {...props} />
      </ProfileEditProvider>
    </GraphQLProvider>
  )
}
